// Pure helpers for the shared Team Directory of professionals.
// No Firestore, no React — just the matching + merge decisions, so the logic
// is easy to unit-test. A thin Firestore wrapper (in firebasefunctions.js)
// performs the actual read-match-write using these.

const lower = (s) => String(s || "").trim().toLowerCase();
const digits = (s) => String(s || "").replace(/\D/g, "");

// Normalize a deal participant (the "Contacts" section row) into a directory
// "pro" shape. Participants carry denormalized name/phone/email plus a `type`
// (the role, e.g. "Lender").
export function normalizePro(participant = {}) {
  return {
    name: String(participant.fullName || participant.name || "").trim(),
    email: String(participant.email || "").trim(),
    phone: String(participant.phone || "").trim(),
    role: String(participant.type || "").trim(),
    company: String(participant.company || participant.organization || "").trim(),
  };
}

// A pro is worth logging only if we can identify it: a name, plus at least one
// of email / phone.
export function isLoggablePro(pro) {
  if (!pro || !pro.name) return false;
  return !!(pro.email || pro.phone);
}

// Find an existing directory entry for a pro. Email first; if the pro has no
// email, fall back to name + phone (digits only). A pro WITH an email that
// matches nothing is treated as new (we don't then guess by name).
export function findDirectoryMatch(entries, pro) {
  const list = entries || [];
  const email = lower(pro.email);
  if (email) {
    return list.find((e) => lower(e.email) === email) || null;
  }
  const nm = lower(pro.name);
  const ph = digits(pro.phone);
  if (!nm || !ph) return null;
  return list.find((e) => lower(e.name) === nm && digits(e.phone) === ph) || null;
}

// True if this (dealId, role) pairing hasn't been recorded on the entry yet —
// keeps re-saving the same deal from adding duplicate history lines.
export function needsHistoryEntry(entry, dealId, role) {
  const hist = (entry && entry.history) || [];
  return !hist.some(
    (h) => String(h.dealId) === String(dealId) && lower(h.role) === lower(role),
  );
}

// Fields to write when updating an existing entry. Pure — returns a new object.
// Fills in any missing contact fields, adds the role if new, and prepends the
// history entry (newest first) when provided.
export function mergeProIntoEntry(entry, pro, historyEntry, nowMs) {
  const roles = Array.isArray(entry.roles) ? [...entry.roles] : [];
  if (pro.role && !roles.includes(pro.role)) roles.push(pro.role);

  const history = Array.isArray(entry.history) ? [...entry.history] : [];
  if (historyEntry) history.unshift(historyEntry);

  return {
    name: entry.name || pro.name,
    email: entry.email || pro.email,
    phone: entry.phone || pro.phone,
    company: entry.company || pro.company,
    roles,
    history,
    updatedAt: nowMs,
  };
}

// Build a brand-new directory entry for a pro with no existing match.
export function buildNewEntry(pro, historyEntry, nowMs) {
  return {
    name: pro.name,
    email: pro.email,
    phone: pro.phone,
    company: pro.company,
    roles: pro.role ? [pro.role] : [],
    history: historyEntry ? [historyEntry] : [],
    createdAt: nowMs,
    updatedAt: nowMs,
  };
}

// Build one history line for a deal.
export function buildHistoryEntry({ dealId, address, role, date }) {
  return {
    dealId: String(dealId || ""),
    address: String(address || ""),
    role: String(role || ""),
    date: date || 0,
  };
}
